<template>
  <div class="detentions-datatable-container">
    <Loader :is-processing="state.isLoading" />
    <ActionPanel
      @tab-clicked="setActiveTab"
      class="mb-3"
      :tabs="tabs"
      :show-export="true"
      @on-export="exportFile"
    >
      <template #bottom-content>
        <div class="d-flex mt-3 flex-wrap">
          <CustomSelect
            v-if="gradeYears"
            v-model="state.filterParams.by_school_year"
            class="min-w-200 me-2"
            :options="gradeYears"
            placeholder="Select year"
            :close-on-select="true"
            :invalid-feedback="null"
            @update:model-value="setCalendarGradeYears"
          ></CustomSelect>
          <CustomSchoolYearSelectDatePicker
            class=""
            v-model="state.selectedDateRange"
            range-input-id="detentions-appointments-date-range"
            :range-options="optionalDateRanges"
            :is-current-school-year-selected="currentSchoolYearSelected"
            :selected-school-year-limit="state.filterParams.by_school_year"
          ></CustomSchoolYearSelectDatePicker>
        </div>
        <div class="text-end">
          <BaseButton solid rounded @click="getData">Submit</BaseButton>
        </div>
      </template>
    </ActionPanel>
    <DataTable :fields="activeTable.fields" :items="activeTable.items">
      <template #image="{ item }">
        <td>
          <img
            class="rounded"
            width="35"
            height="35"
            :src="
              item.user.avatar
                ? item.user.avatar
                : '/img/avatars/user-avatar.jpeg'
            "
          />
        </td>
      </template>
      <template #first_name="{ item }">
        <td>{{ item.user.first_name }}</td>
      </template>
      <template #last_name="{ item }">
        <td>{{ item.user.last_name }}</td>
      </template>
      <template #grade_year="{ item }">
        <td>{{ item.user.grade_year }}</td>
      </template>
      <template #step="{ item }">
        <td>{{ item.step ? item.step : "" }}</td>
      </template>
      <template #status="{ item }">
        <td><DetentionStatusPill :status="item.status" /></td>
      </template>
    </DataTable>
    <Pagination
      v-if="state && state.pagination && state.pagination.pages > 1"
      :active-page="state.pagination.activePage"
      :pages="state.pagination.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      @update:active-page="setActivePage"
    />
  </div>
</template>

<script>
import Loader from "@/v3components/shared/Loader/Loader.vue"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import BaseButton from "@/v3components/shared/Buttons/BaseButton.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import { useStore } from "vuex"
import moment from "moment-timezone"
import { onMounted, computed, reactive } from "vue"
import { useGradeYears } from "@/composables/useGradeYears.js"
import CustomSelect from "@/v3components/shared/Form/CustomSelect.vue"
import CustomSchoolYearSelectDatePicker from "@/v3components/shared/Form/CustomSchoolYearSelectDatePicker.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import DetentionStatusPill from "../shared/Tardy/DetentionStatusPill.vue"
import helpers from "../../helpers/index.js"
import download from "@/helpers/downloadCSV.js"

export default {
  name: "DetentionsAndAppointmentsTable",
  components: {
    Loader,
    ActionPanel,
    BaseButton,
    DataTable,
    CustomSelect,
    CustomSchoolYearSelectDatePicker,
    Pagination,
    DetentionStatusPill
  },
  setup() {
    const store = useStore()
    const { gradeYears, currentSchoolYear, getMinDate, getMaxDate } =
      useGradeYears()

    const state = reactive({
      isLoading: false,
      activeTab: null,
      filterParams: {
        by_school_year: [],
        dates:
          moment().format("MM/DD/YYYY") + " - " + moment().format("MM/DD/YYYY")
      },
      selectedDateRange: null,
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        per_page: { label: "25", value: 25 }
      }
    })

    onMounted(() => {
      state.activeTab = tabs[0]
      state.filterParams.by_school_year = gradeYears.value
        ? gradeYears.value[gradeYears.value.length - 1]
        : state.filterParams.by_school_year
    })

    const activeTable = computed(() => {
      return isDetentionMode.value
        ? {
            items: detentions.value,
            fields: detentionTableFields
          }
        : {
            items: appointments.value,
            fields: appointmentTableFileds
          }
    })

    const detentions = computed(
      () => store.getters["detentionsAppointments/detentions"]
    )
    const appointments = computed(
      () => store.getters["detentionsAppointments/appointments"]
    )
    const isDetentionMode = computed(
      () => state.activeTab && state.activeTab.value === "DETENTION"
    )

    const minDate = computed(() => {
      return getMinDate(state.filterParams.by_school_year)
    })

    const maxDate = computed(() => {
      return getMaxDate(state.filterParams.by_school_year)
    })

    const currentSchoolYearSelected = computed(() => {
      return (
        state.filterParams.by_school_year?.label ==
        currentSchoolYear.value?.label
      )
    })

    const optionalDateRanges = computed(() => {
      const selectedSchoolYear = state.filterParams?.by_school_year?.value

      if (!selectedSchoolYear || !minDate.value) {
        return [
          {
            label: "Today",
            id: "",
            range: helpers.stringDateRange(moment(), moment())
          }
        ]
      }

      const getRange = (days) => {
        const today = moment()
        const start = today.clone().subtract(days, "days")

        const minSchoolYearDate = moment({
          year: minDate.value.getFullYear(),
          month: minDate.value.getMonth(),
          date: minDate.value.getDate()
        })

        const adjustedStart = start.isBefore(minSchoolYearDate)
          ? minSchoolYearDate
          : start

        return helpers.stringDateRange(adjustedStart, today)
      }

      return [
        {
          label: "Today",
          id: "",
          range: helpers.stringDateRange(moment(), moment())
        },
        {
          label: "Yesterday",
          id: "",
          range: helpers.stringDateRange(
            moment().subtract(1, "days"),
            moment().subtract(1, "days")
          )
        },
        {
          label: "Last 7 days",
          id: "",
          range: getRange(7)
        },
        {
          label: "Last 14 days",
          id: "",
          range: getRange(14)
        },
        {
          label: "Last 30 days",
          id: "",
          range: getRange(30)
        },
        {
          label: "This month",
          id: "",
          range: helpers.stringDateRange(
            moment().startOf("month"),
            moment().endOf("month")
          )
        },
        {
          label: "This year",
          id: "",
          range:
            helpers.currTzDate(minDate.value) +
            " - " +
            helpers.currTzDate(maxDate.value)
        },
        {
          label: "Custom Range",
          id: "custom",
          range: null
        }
      ]
    })

    const setCalendarGradeYears = () => {
      state.selectedDateRange = `${helpers.currTzDate(minDate.value)} - ${helpers.currTzDate(maxDate.value)}`
    }

    const getData = () => {
      const data = {
        between_dates: state.selectedDateRange
          ? state.selectedDateRange
              .replace(/\//g, ".")
              .split(" - ")
              .map((date) => moment(date, "MM/DD/YYYY").format("MM/DD/YYYY"))
          : [moment().format("MM/DD/YYYY"), moment().format("MM/DD/YYYY")],
        type: state.activeTab.value,
        per_page: state.pagination.per_page.value,
        page: state.pagination.activePage
      }
      state.isLoading = true
      store
        .dispatch("detentionsAppointments/getDetentions", data)
        .then((response) => {
          state.isLoading = false
          if (response && response.data && response.data.meta) {
            const data = response.data.meta

            setPagination({
              total: data.total,
              from: data.from,
              to: data.to,
              pages: Math.ceil(data.total / data.per_page),
              activePage: Number(state.pagination.activePage)
            })
          }
        })
    }

    const setPagination = (data) => {
      state.pagination = Object.assign(state.pagination, data)
    }

    const setActivePage = (page) => {
      state.pagination.activePage = page
      getData()
    }

    const detentionTableFields = [
      { key: "image", label: "Image", sorter: false, filter: false },
      { key: "first_name", label: "First name", sorter: false, filter: false },
      { key: "last_name", label: "Last name", sorter: false, filter: false },
      { key: "grade_year", label: "Grad year", sorter: false, filter: false },
      {
        key: "step",
        label: "Infraction step",
        sorter: false,
        filter: false
      },
      {
        key: "action",
        label: "Detention type",
        sorter: false,
        filter: false
      },
      {
        key: "status",
        label: "Status",
        sorter: false,
        filter: false
      }
    ]

    const appointmentTableFileds = [
      { key: "image", label: "Image", sorter: false, filter: false },
      { key: "first_name", label: "First name", sorter: false, filter: false },
      { key: "last_name", label: "Last name", sorter: false, filter: false },
      { key: "grade_year", label: "Grad year", sorter: false, filter: false },
      {
        key: "step",
        label: "Infraction step",
        sorter: false,
        filter: false
      },
      {
        key: "action",
        label: "Appointment type",
        sorter: false,
        filter: false
      }
    ]
    //table
    const tabs = [
      {
        label: "Detentions",
        value: "DETENTION",
        action: getData
      },
      {
        label: "Appointments",
        value: "APT",
        action: getData
      }
    ]
    const setActiveTab = (tab) => {
      state.activeTab = tab
      resetFilterValues()
      try {
        tab.action()
      } catch (error) {
        throw new Error(error)
      }
    }
    const resetFilterValues = () => {
      state.filterParams.by_school_year = gradeYears.value
        ? gradeYears.value[gradeYears.value.length - 1]
        : state.filterParams.by_school_year
      state.selectedDateRange = optionalDateRanges.value[0].range
    }
    const exportFile = () => {
      const params = {
        between_dates: state.selectedDateRange
          ? state.selectedDateRange
              .replace(/\//g, ".")
              .split(" - ")
              .map((date) => moment(date, "MM/DD/YYYY").format("MM/DD/YYYY"))
          : [moment().format("MM/DD/YYYY"), moment().format("MM/DD/YYYY")],
        type: state.activeTab.value,
        page: state.pagination.activePage,
        per_page: [25, 50, 100].includes(state.pagination.per_page.value)
          ? state.pagination.per_page.value
          : "all"
      }
      store
        .dispatch("detentionsAppointments/exportFile", params)
        .then((response) => {
          if (response) {
            download.CSVExport(
              response.data,
              state.activeTab.value === "DETENTION"
                ? "tardy_detentions.csv"
                : "tardy_appointments.csv"
            )
          }
        })
    }

    return {
      tabs,
      setActiveTab,
      minDate,
      maxDate,
      gradeYears,
      currentSchoolYearSelected,
      optionalDateRanges,
      setCalendarGradeYears,
      activeTable,
      isDetentionMode,
      exportFile,
      state,
      getData,
      setActivePage
    }
  }
}
</script>

<style>
.min-w-200 {
  min-width: 200px;
}
</style>
